document.documentElement.classList.add('lc-js');

(() => {
  const rootRoute = () => window.Shopify?.routes?.root || '/';
  const formatMoney = (cents, currency) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency: currency || 'CAD'
      }).format(value);
    } catch (_error) {
      return '$' + value.toFixed(2);
    }
  };

  const inferSize = (text = '') => {
    const normalized = String(text).toLowerCase().replace(/\s+/g, '');
    if (normalized.includes('16oz')) return '16oz';
    if (normalized.includes('4oz')) return '4oz';
    if (normalized.includes('2oz')) return '2oz';
    if (normalized.includes('4l')) return '4l';
    return '';
  };

  const initializeMenu = (root = document) => {
    root.querySelectorAll('[data-lc-menu-toggle]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      const nav = document.getElementById(button.getAttribute('aria-controls'));
      if (!nav) return;
      const close = () => {
        button.setAttribute('aria-expanded', 'false');
        nav.classList.remove('is-open');
        document.body.classList.remove('lc-menu-open');
      };
      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        nav.classList.toggle('is-open', open);
        document.body.classList.toggle('lc-menu-open', open);
      });
      nav.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
      document.addEventListener('keydown', (event) => event.key === 'Escape' && close());
    });
  };

  const initializeLocalization = (root = document) => {
    root.querySelectorAll('[data-lc-country-select]').forEach((select) => {
      if (select.dataset.initialized === 'true') return;
      select.dataset.initialized = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  };

  const initializeReveal = (root = document) => {
    const items = [...root.querySelectorAll('.lc-reveal:not([data-lc-reveal-ready])')];
    if (!items.length) return;
    items.forEach((item) => item.setAttribute('data-lc-reveal-ready', 'true'));
    if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: 0.06 });
    items.forEach((item) => observer.observe(item));
  };

  const normalizeSelectorData = (scope, select) => {
    [...select.options].forEach((option) => {
      const size = inferSize(option.dataset.title || option.textContent);
      if (size) option.dataset.size = size;
    });
    scope.querySelectorAll('[data-lc-format-option]').forEach((button) => {
      const option = [...select.options].find((item) => item.value === button.dataset.variantId);
      const size = inferSize(option?.dataset.title || option?.textContent || '');
      if (size) button.dataset.size = size;
    });
  };

  const updateVariantScope = (scope, select) => {
    const option = select.options[select.selectedIndex];
    if (!option) return;
    const available = option.dataset.available === 'true';
    const price = option.dataset.price;
    const compareAt = option.dataset.compareAt;
    const currency = option.dataset.currency || 'CAD';
    const size = option.dataset.size || inferSize(option.dataset.title || option.textContent);
    const title = option.dataset.title || option.textContent.trim();

    scope.querySelectorAll('[data-lc-price]').forEach((element) => {
      element.textContent = formatMoney(price, currency);
    });
    scope.querySelectorAll('[data-lc-compare-price]').forEach((element) => {
      const show = Number(compareAt) > Number(price);
      element.textContent = show ? formatMoney(compareAt, currency) : '';
      element.hidden = !show;
    });
    scope.querySelectorAll('[data-lc-selected-title]').forEach((element) => {
      element.textContent = title;
    });
    scope.querySelectorAll('[data-lc-add-button]').forEach((button) => {
      button.disabled = !available;
      button.textContent = available ? button.dataset.availableText : button.dataset.soldOutText;
    });
    scope.querySelectorAll('[data-lc-size-option]').forEach((button) => {
      const selected = button.dataset.lcSizeOption === size;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    scope.querySelectorAll('[data-lc-format-option]').forEach((button) => {
      const selected = button.dataset.variantId === option.value;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    const variantImage = option.dataset.image;
    if (variantImage) {
      scope.querySelectorAll('[data-lc-packshot-image]').forEach((image) => {
        image.src = variantImage;
        image.removeAttribute('srcset');
      });
    }
  };

  const chooseSize = (scope, select, size) => {
    const formatButtons = [...scope.querySelectorAll('[data-lc-format-option]')];
    formatButtons.forEach((button) => { button.hidden = button.dataset.size !== size; });
    const matching = formatButtons.filter((button) => button.dataset.size === size);
    const glass = matching.find((button) => button.dataset.sellable === 'true' && /glass/i.test(button.textContent));
    const available = matching.find((button) => button.dataset.sellable === 'true');
    const next = glass || available || matching[0];
    if (!next) return;
    select.value = next.dataset.variantId;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const initializeVariantSelectors = (root = document) => {
    root.querySelectorAll('[data-lc-product-scope]').forEach((scope) => {
      if (scope.dataset.lcVariantsReady === 'true') return;
      const select = scope.querySelector('[data-lc-variant-select]');
      if (!select) return;
      scope.dataset.lcVariantsReady = 'true';
      normalizeSelectorData(scope, select);

      const params = new URLSearchParams(window.location.search);
      if (!params.has('variant')) {
        const preferred = [...select.options].find((option) => {
          const title = String(option.dataset.title || option.textContent).toLowerCase().replace(/\s+/g, '');
          return title.includes('4oz') && title.includes('glass') && option.dataset.available === 'true';
        });
        if (preferred) select.value = preferred.value;
      }

      scope.querySelectorAll('[data-lc-size-option]').forEach((button) => {
        button.addEventListener('click', () => chooseSize(scope, select, button.dataset.lcSizeOption));
      });
      scope.querySelectorAll('[data-lc-format-option]').forEach((button) => {
        button.addEventListener('click', () => {
          select.value = button.dataset.variantId;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      select.addEventListener('change', () => updateVariantScope(scope, select));
      updateVariantScope(scope, select);
    });
  };

  const cartRequestOptions = (body) => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.getSectionsToRender) {
      body.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id));
      body.append('sections_url', window.location.pathname);
    }
    return { cartDrawer, options: { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body } };
  };

  const renderCart = (cartDrawer, response) => {
    if (cartDrawer?.renderContents && response.sections) cartDrawer.renderContents(response);
    else window.location.assign(rootRoute() + 'cart');
  };

  const initializeProductForms = (root = document) => {
    root.querySelectorAll('[data-lc-product-form]').forEach((form) => {
      if (form.dataset.lcSubmitInitialized === 'true') return;
      form.dataset.lcSubmitInitialized = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const formId = form.getAttribute('id');
        const buttons = document.querySelectorAll(`[form="${formId}"][data-lc-add-button], #${formId} [data-lc-add-button]`);
        const status = form.querySelector('[data-lc-form-status]');
        buttons.forEach((button) => {
          button.disabled = true;
          button.dataset.originalText = button.textContent;
          button.textContent = button.dataset.loadingText;
        });
        if (status) status.textContent = '';
        try {
          const payload = new FormData(form);
          const request = cartRequestOptions(payload);
          const response = await fetch(rootRoute() + 'cart/add.js', request.options);
          const json = await response.json();
          if (!response.ok || json.status) throw new Error(json.description || 'Cart request failed');
          renderCart(request.cartDrawer, json);
        } catch (error) {
          buttons.forEach((button) => {
            button.disabled = false;
            button.textContent = button.dataset.originalText || button.dataset.availableText;
          });
          if (status) status.textContent = error.message;
        }
      });
    });
  };

  const addItems = async (items) => {
    const payload = new FormData();
    items.forEach((item, index) => {
      payload.append(`items[${index}][id]`, item.id);
      payload.append(`items[${index}][quantity]`, item.quantity || 1);
    });
    const request = cartRequestOptions(payload);
    const response = await fetch(rootRoute() + 'cart/add.js', request.options);
    const json = await response.json();
    if (!response.ok || json.status) throw new Error(json.description || 'Cart request failed');
    renderCart(request.cartDrawer, json);
  };

  const initializeQuickAdds = (root = document) => {
    root.querySelectorAll('[data-lc-quick-add]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', async () => {
        const original = button.textContent;
        button.disabled = true;
        button.textContent = button.dataset.loadingText || original;
        try { await addItems([{ id: button.dataset.variantId, quantity: 1 }]); }
        catch (_error) { button.disabled = false; button.textContent = original; }
      });
    });

    root.querySelectorAll('[data-lc-bundle-add]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', async () => {
        const original = button.textContent;
        const status = button.closest('.lc-bundle-card')?.querySelector('[data-lc-bundle-status]');
        let items = [];
        try { items = JSON.parse(button.dataset.items || '[]'); } catch (_error) { items = []; }
        if (!items.length) return;
        button.disabled = true;
        button.textContent = button.dataset.loadingText || original;
        if (status) status.textContent = '';
        try { await addItems(items); }
        catch (_error) {
          button.disabled = false;
          button.textContent = original;
          if (status) status.textContent = button.dataset.errorText;
        }
      });
    });
  };

  const initializeGallery = (root = document) => {
    root.querySelectorAll('[data-lc-gallery]').forEach((gallery) => {
      if (gallery.dataset.initialized === 'true') return;
      gallery.dataset.initialized = 'true';
      const main = gallery.querySelector('[data-lc-gallery-main]');
      gallery.querySelectorAll('[data-lc-gallery-thumb]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!main || !button.dataset.src) return;
          main.src = button.dataset.src;
          main.removeAttribute('srcset');
          main.alt = button.dataset.alt || '';
          gallery.querySelectorAll('[data-lc-gallery-thumb]').forEach((thumb) => {
            const selected = thumb === button;
            thumb.classList.toggle('is-active', selected);
            thumb.setAttribute('aria-pressed', String(selected));
          });
        });
      });
    });
  };

  const initializeStickyBuy = (root = document) => {
    root.querySelectorAll('[data-lc-sticky-buy]').forEach((bar) => {
      if (bar.dataset.initialized === 'true') return;
      bar.dataset.initialized = 'true';
      const scope = bar.closest('[data-lc-product-scope]');
      const source = scope?.querySelector('[data-lc-purchase-card], .lc-hero__purchase');
      if (!source || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0.05 });
      observer.observe(source);
    });
  };

  const initializeWaitlist = (root = document) => {
    root.querySelectorAll('[data-lc-waitlist-form]').forEach((form) => {
      if (form.dataset.initialized === 'true') return;
      form.dataset.initialized = 'true';
      const tags = form.querySelector('[data-lc-waitlist-tags]');
      const country = form.querySelector('[data-lc-waitlist-country]');
      const pet = form.querySelector('[data-lc-waitlist-pet]');
      const update = () => {
        const countryTag = `waitlist_country_${String(country?.value || '').toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
        const petTags = pet?.value === 'both' ? ['dog_owner', 'cat_owner', 'multi_pet'] : [pet?.value === 'cat' ? 'cat_owner' : 'dog_owner'];
        if (tags) tags.value = ['international_waitlist', countryTag, ...petTags].join(',');
      };
      country?.addEventListener('change', update);
      pet?.addEventListener('change', update);
      form.addEventListener('submit', update);
      update();
    });
  };

  const removeHamiltonOverlay = (root = document) => {
    root.querySelectorAll('.lc-hero__made').forEach((element) => element.remove());
  };

  const initialize = (root = document) => {
    removeHamiltonOverlay(root);
    initializeMenu(root);
    initializeLocalization(root);
    initializeReveal(root);
    initializeVariantSelectors(root);
    initializeProductForms(root);
    initializeQuickAdds(root);
    initializeGallery(root);
    initializeStickyBuy(root);
    initializeWaitlist(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
